import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { maskNumber, parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const NetSalaryCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [grossSalary, setGrossSalary] = useState('5.000,00');
  const [dependents, setDependents] = useState('0');
  const [otherDeductions, setOtherDeductions] = useState('0,00');
  const [pensionPlan, setPensionPlan] = useState('0,00');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const salary = parseFloat(parseBrazilianNumber(grossSalary)) || 0;
    const deps = parseInt(dependents) || 0;
    const other = parseFloat(parseBrazilianNumber(otherDeductions)) || 0;
    const pension = parseFloat(parseBrazilianNumber(pensionPlan)) || 0;

    if (salary <= 0) return;

    // Tabela INSS 2024
    let inss = 0;
    let inssRate = 0;

    if (salary <= 1412.00) {
      inss = salary * 0.075;
    } else if (salary <= 2666.68) {
      inss = (1412.00 * 0.075) + ((salary - 1412.00) * 0.09);
    } else if (salary <= 4000.03) {
      inss = (1412.00 * 0.075) + ((2666.68 - 1412.00) * 0.09) + ((salary - 2666.68) * 0.12);
    } else if (salary <= 7786.02) {
      inss = (1412.00 * 0.075) + ((2666.68 - 1412.00) * 0.09) + ((4000.03 - 2666.68) * 0.12) + ((salary - 4000.03) * 0.14);
    } else {
      inss = 908.85; // Teto INSS
    }

    inssRate = (inss / salary) * 100;

    // Base de cálculo IRPF
    const baseWithDeductions = salary - inss - (deps * 189.59) - other - pension;
    const baseSimplified = salary - inss - 528.00; // Desconto simplificado

    // Escolher o mais vantajoso
    const baseIR = Math.max(0, Math.min(baseWithDeductions, baseSimplified));
    const isSimplified = baseSimplified < baseWithDeductions;

    // Cálculo IRPF
    let irpf = 0;
    let irpfRate = 0;

    if (baseIR <= 2112.00) {
      irpf = 0;
      irpfRate = 0;
    } else if (baseIR <= 2826.65) {
      irpf = (baseIR * 0.075) - 158.40;
      irpfRate = 7.5;
    } else if (baseIR <= 3751.05) {
      irpf = (baseIR * 0.15) - 370.40;
      irpfRate = 15.0;
    } else if (baseIR <= 4664.68) {
      irpf = (baseIR * 0.225) - 651.73;
      irpfRate = 22.5;
    } else {
      irpf = (baseIR * 0.275) - 884.96;
      irpfRate = 27.5;
    }

    irpf = Math.max(0, irpf);

    const totalDeductions = inss + irpf + other + pension;
    const netSalary = salary - totalDeductions;
    const effectiveDiscountRate = (totalDeductions / salary) * 100;

    // Benefícios estimados (vale transporte, alimentação, etc.)
    const estimatedBenefits = {
      transportAllowance: Math.min(salary * 0.06, 320), // Até 6% ou R$ 320
      mealAllowance: Math.min(salary * 0.20, 600), // Até 20% ou R$ 600
      totalEstimated: 0
    };
    estimatedBenefits.totalEstimated = estimatedBenefits.transportAllowance + estimatedBenefits.mealAllowance;

    const res = {
      grossSalary: salary,
      netSalary,
      inss,
      irpf,
      otherDeductions: other,
      pensionPlan: pension,
      totalDeductions,
      inssRate: inssRate.toFixed(2),
      irpfRate: irpfRate.toFixed(2),
      effectiveDiscountRate: effectiveDiscountRate.toFixed(2),
      method: isSimplified ? 'Simplificado' : 'Completo',
      estimatedBenefits
    };

    setResult(res);

    onResultChange([
      { label: 'Salário Bruto', value: `R$ ${res.grossSalary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'INSS', value: `R$ ${res.inss.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'IRPF', value: `R$ ${res.irpf.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Salário Líquido', value: `R$ ${res.netSalary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Descontos Totais', value: `${res.effectiveDiscountRate}%` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border">
        <h2 className="text-xl font-bold text-theme-text mb-3">Salário Líquido</h2>
        <p className="text-theme-muted mb-4">
          Calcule seu salário líquido com todos os descontos obrigatórios (INSS, IRPF) e opcionais.
        </p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Salário Bruto (R$)"
            value={grossSalary}
            onChange={setGrossSalary}
            required
            isNumeric
            type="text"
          />
          <FormField
            label="Número de Dependentes"
            value={dependents}
            onChange={setDependents}
            isInteger
            type="text"
          />
          <FormField
            label="Outras Deduções (R$)"
            value={otherDeductions}
            onChange={setOtherDeductions}
            isNumeric
            type="text"
          />
          <FormField
            label="Previdência Privada (R$)"
            value={pensionPlan}
            onChange={setPensionPlan}
            isNumeric
            type="text"
          />
        </div>

        <div className="mt-8 flex justify-center">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Salário Líquido
          </button>
        </div>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ResultCard
              title="Salário Líquido"
              value={`R$ ${result.netSalary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
              color="emerald"
            />
            <ResultCard
              title="INSS"
              value={`R$ ${result.inss.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
              color="blue"
            />
            <ResultCard
              title="IRPF"
              value={`R$ ${result.irpf.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
              color="rose"
            />
            <ResultCard
              title="Descontos Totais"
              value={`${result.effectiveDiscountRate}%`}
              color="slate"
            />
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
            <h3 className="text-lg font-bold text-theme-text mb-4">Benefícios Estimados</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-theme-result p-4 rounded-xl">
                <p className="text-xs font-black text-theme-muted uppercase mb-1">Vale Transporte</p>
                <p className="text-lg font-bold text-indigo-600">R$ {result.estimatedBenefits.transportAllowance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="bg-theme-result p-4 rounded-xl">
                <p className="text-xs font-black text-theme-muted uppercase mb-1">Vale Alimentação</p>
                <p className="text-lg font-bold text-purple-600">R$ {result.estimatedBenefits.mealAllowance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          </div>
        </>
      )}

      <EducationalBlock
        title="Dicas e Informações"
        tips={[
          "O desconto simplificado de R$ 528,00 é aplicado automaticamente se for melhor para você.",
          "INSS tem um teto máximo de desconto.",
          "Dedução por dependente é fixa em R$ 189,59 cada."
        ]}
      />
    </div>
  );
};

export default NetSalaryCalculator;
